import { memo, useMemo } from "react";
import { ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { trackContributorClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import type { RankedContributor } from "@/lib/types";

interface ContributorCardProps {
  contributor: RankedContributor;
  highlighted: boolean;
  onHover: (login: string | null) => void;
}

export const ContributorCard = memo(function ContributorCard({ contributor, highlighted, onHover }: ContributorCardProps) {
  const contributions = useMemo(
    () => Object.entries(contributor.contributions).sort((left, right) => right[1] - left[1]),
    [contributor.contributions],
  );

  return (
    <Card
      className={cn(
        "transition-colors hover:bg-muted/35",
        highlighted ? "bg-primary/5" : "bg-background/55",
      )}
      onMouseEnter={() => onHover(contributor.login)}
      onMouseLeave={() => onHover(null)}
    >
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex items-start gap-4">
          <img
            src={contributor.avatar_url}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-12 rounded-[3px] border border-border object-cover"
          />
          <div className="min-w-0 flex-1">
            <a
              className="inline-flex items-center gap-1 font-semibold text-foreground hover:text-primary"
              href={contributor.profile_url}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackContributorClick(contributor.login)}
            >
              {contributor.login}
              <ArrowUpRight className="size-4" />
            </a>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {contributions.map(([repoName, count]) => (
            <Badge key={`${contributor.login}-${repoName}`} variant="secondary">
              {repoName}: {count}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});
